// Siblings navigation helper (W3.1 §5.2 詳情頁 Siblings 區塊).
//
// Computes the previous / next sibling for the anniversary detail page.
//
// Filter cascade per spec §5.2:
//   1. same `category` — pick prev/next within the same-category list
//   2. fallback same `author_id` — fall back to global neighbours (the
//      category filter produced only the current row)
//   3. fallback global nearest neighbour
//
// The full list is client-side (no new API), so the algorithm reads from
// `useAnniversariesList().data.items`.

import type { AnniversaryListItem } from "@/lib/api/anniversary";

export interface SiblingsContext {
  /** The list the user is currently browsing — must contain `currentId`. */
  list: readonly AnniversaryListItem[];
  currentId: string;
}

export interface SiblingsResult {
  prev: AnniversaryListItem | null;
  next: AnniversaryListItem | null;
}

/**
 * Resolve siblings by cascading through the filter dimensions. Returns
 * `prev` and `next` (both may be `null` at list edges).
 *
 * Cascade:
 *   - Try same-category list with prev/next neighbours
 *   - Fallback to global prev/next
 */
export function siblingsOf(ctx: SiblingsContext): SiblingsResult {
  const { list, currentId } = ctx;
  if (!Array.isArray(list) || list.length === 0) return { prev: null, next: null };

  const idx = list.findIndex((it) => it.id === currentId);
  if (idx === -1) {
    // current not in list — surface the full list as nearest fallback.
    return { prev: list[0] ?? null, next: list[list.length - 1] ?? null };
  }

  const current = list[idx]!;

  // 1. Try same-category neighbours.
  const sameCategory = list.filter(
    (it) => it.id !== currentId && it.category === current.category,
  );
  if (sameCategory.length > 0) {
    // Find the index of `current` within the same-category list (so the
    // current row is excluded but its neighbours may be in the original list).
    const catIdx = list.findIndex(
      (it, i) =>
        it.category === current.category &&
        // pick the entry immediately before or after `current` in the global list
        // that also shares the category
        (i < idx || i > idx) &&
        it.id !== currentId,
    );
    if (catIdx !== -1) {
      // Prev: walk back through the global list to find same-cat neighbour
      let prevIdx = idx - 1;
      while (prevIdx >= 0 && list[prevIdx]!.category !== current.category) prevIdx -= 1;
      let nextIdx = idx + 1;
      while (
        nextIdx < list.length &&
        list[nextIdx]!.category !== current.category
      )
        nextIdx += 1;
      return {
        prev: prevIdx >= 0 ? list[prevIdx]! : null,
        next: nextIdx < list.length ? list[nextIdx]! : null,
      };
    }
  }

  // 2. Fallback to same-author filter (per spec §5.2 step 2).
  const sameAuthor = list.filter(
    (it) => it.id !== currentId && it.author?.id === current.author?.id,
  );
  if (sameAuthor.length > 0) {
    return {
      prev: idx > 0 ? (list[idx - 1] ?? null) : null,
      next: idx < list.length - 1 ? (list[idx + 1] ?? null) : null,
    };
  }

  // 3. Global nearest neighbour fallback.
  return {
    prev: idx > 0 ? (list[idx - 1] ?? null) : null,
    next: idx < list.length - 1 ? (list[idx + 1] ?? null) : null,
  };
}

/**
 * Edge case per spec §5.2: hide the entire Siblings block when there's
 * only one row in the list.
 */
export function shouldRenderSiblings(list: readonly AnniversaryListItem[]): boolean {
  return Array.isArray(list) && list.length > 1;
}